import React, { useState } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight,
  Trophy,
  Users,
  UserCircle,
  Calendar,
  BarChart3,
  Globe,
  CreditCard,
  Target,
  Share2,
  Shield
} from 'lucide-react';
import { Button } from './ui/button';

const demoSlides = [
  {
    id: 1,
    title: "Dashboard Overview",
    subtitle: "Your Tournament Command Center",
    icon: BarChart3,
    color: "emerald",
    image: "/demo/dashboard.png",
    features: [
      "Real-time tournament statistics at a glance",
      "Track teams, players, and match progress",
      "Quick access to all management tools",
      "Activity feed showing recent updates"
    ],
    description: "Get a complete overview of your tournament with live stats, upcoming matches, and recent activity all in one place."
  },
  {
    id: 2,
    title: "Team Management",
    subtitle: "Organize Your Teams Effortlessly",
    icon: Users,
    color: "blue",
    image: "/demo/teams.png",
    features: [
      "Create and manage unlimited teams",
      "Add team logos and customize colors",
      "Track team statistics automatically",
      "View team rosters at a glance"
    ],
    description: "Easily create teams, add logos, and manage rosters. All team stats are calculated automatically as matches are played."
  },
  {
    id: 3,
    title: "Player Profiles",
    subtitle: "Track Every Player's Performance",
    icon: UserCircle,
    color: "purple",
    image: "/demo/players.png",
    features: [
      "Detailed player profiles with stats",
      "Track goals, assists, and appearances",
      "Assign players to teams easily",
      "Player leaderboards and rankings"
    ],
    description: "Maintain comprehensive player profiles with automatic stat tracking. See who's leading in goals, assists, and more."
  },
  {
    id: 4,
    title: "Match Scheduling",
    subtitle: "Schedule & Score Matches",
    icon: Calendar,
    color: "orange",
    image: "/demo/matches.png",
    features: [
      "Easy match scheduling interface",
      "Update scores in real-time",
      "Track goal scorers per match",
      "Match history and statistics"
    ],
    description: "Schedule matches between teams and update scores live. Track individual goal scorers and maintain complete match records."
  },
  {
    id: 5,
    title: "Referee Assignment",
    subtitle: "Professional Match Officiating",
    icon: Shield,
    color: "teal",
    image: "/demo/referees.png",
    features: [
      "Assign Main Referee, Assistants & Fourth Official",
      "Manage a full roster of certified referees",
      "Public referee profiles with experience & bio",
      "Player voting system to rate officiating"
    ],
    description: "Assign up to four officials per match from your referee roster. Each referee gets a public profile with experience tracking and post-match player ratings. Available on Pro & Enterprise plans."
  },
  {
    id: 6,
    title: "Match Events",
    subtitle: "Goals, Assists & Cards",
    icon: Target,
    color: "red",
    image: "/demo/scorers.png",
    features: [
      "Record goals, assists, yellow & red cards",
      "Track player name and minute of incident",
      "Event timeline displayed on completed matches",
      "Automatic player stat updates"
    ],
    description: "Capture every key moment — goals, assists, and disciplinary cards with the player's name and exact minute. Events appear as a timeline on match cards."
  },
  {
    id: 7,
    title: "Live Standings",
    subtitle: "Automatic League Tables",
    icon: Trophy,
    color: "yellow",
    image: "/demo/standings.png",
    features: [
      "Auto-calculated league standings",
      "Points, goal difference, form tracking",
      "Win/Draw/Loss records",
      "Head-to-head tiebreakers"
    ],
    description: "Standings are calculated automatically after each match. Track points, goal difference, and recent form for every team."
  },
  {
    id: 8,
    title: "Public Tournament Page",
    subtitle: "Share With Fans & Players",
    icon: Globe,
    color: "cyan",
    image: "/demo/public.png",
    features: [
      "Shareable public tournament link",
      "Live standings visible to everyone",
      "Match schedules and results",
      "Mobile-friendly design"
    ],
    description: "Share your tournament with a custom public link. Fans and players can view standings, fixtures, and results anytime."
  },
  {
    id: 9,
    title: "Flexible Plans",
    subtitle: "Pricing That Scales With You",
    icon: CreditCard,
    color: "green",
    image: "/demo/pricing.png",
    features: [
      "Free tier to get started",
      "Starter plan for small leagues",
      "Pro plan for serious organizers",
      "Enterprise for large organizations"
    ],
    description: "Start free and upgrade as you grow. From casual tournaments to professional leagues, we have a plan for you."
  }
];

const colorClasses = {
  emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  red: "bg-red-500/20 text-red-400 border-red-500/30",
  teal: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  cyan: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  green: "bg-green-500/20 text-green-400 border-green-500/30",
};

const iconBgClasses = {
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  purple: "bg-purple-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  teal: "bg-teal-500",
  yellow: "bg-yellow-500",
  cyan: "bg-cyan-500",
  green: "bg-green-500",
};

export default function DemoModal({ isOpen, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!isOpen) return null;

  const slide = demoSlides[currentSlide];
  const Icon = slide.icon;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % demoSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + demoSlides.length) % demoSlides.length);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-5xl bg-[#0f0f10] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${iconBgClasses[slide.color]} rounded-lg flex items-center justify-center`}>
              <Icon className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">{slide.title}</h2>
              <p className="text-gray-400 text-sm">{slide.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left - Visual */}
            <div className={`${colorClasses[slide.color]} border rounded-xl p-8 flex flex-col items-center justify-center min-h-[300px]`}>
              <div className={`w-24 h-24 ${iconBgClasses[slide.color]} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                <Icon className="text-white" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-white text-center mb-2">{slide.title}</h3>
              <p className="text-gray-300 text-center">{slide.description}</p>
            </div>

            {/* Right - Features */}
            <div className="flex flex-col justify-center">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Shield size={18} className="text-emerald-500" />
                Key Features
              </h4>
              <ul className="space-y-3">
                {slide.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className={`w-6 h-6 ${iconBgClasses[slide.color]} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-gray-400 text-sm mb-3">Ready to get started?</p>
                <Button
                  onClick={onClose}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  Start Free Trial
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 bg-white/5">
          {/* Slide indicators */}
          <div className="flex items-center gap-2">
            {demoSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'w-6 bg-emerald-500' 
                    : 'bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>

          {/* Slide counter */}
          <span className="text-gray-400 text-sm">
            {currentSlide + 1} / {demoSlides.length}
          </span>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft size={20} />
              Prev
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              className="text-gray-400 hover:text-white"
            >
              Next
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
